import type {
  QuotaPolicy,
  ExclusionCategory,
  QuotaScheduleRow,
  QuotaRunResponse,
} from '@cca/xero-shared';

// The shared quota response DTOs live in @cca/xero-shared (one contract, pinned at
// both quota workers) — re-exported here so existing imports of this module keep
// working.
export type { ExclusionCategory, QuotaScheduleRow, QuotaRunResponse };

// ── Session token (header auth, like the finance portal) ───────────────────────
const TOKEN_KEY = 'quota_model_session_token';
export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string): void => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY);

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('X-Requested-With', 'XMLHttpRequest');
  if (init.body) headers.set('Content-Type', 'application/json');
  const token = getToken();
  if (token) headers.set('X-Session-Token', token);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    if (res.status === 401) {
      clearToken();
      window.dispatchEvent(new CustomEvent('model:session-expired'));
    }
    throw new ApiError(String(json.error ?? `HTTP ${res.status}`), res.status);
  }
  return json as T;
}

// ── Shared types ──────────────────────────────────────────────────────────────
// ExclusionCategory / QuotaScheduleRow / QuotaRunResponse now live in
// @cca/xero-shared (one pinned contract for both quota workers + both web apps);
// re-exported at the top of this module.

export interface ModelChurch {
  church_id: string;
  charity_registration_number: string | null;
  name: string;
  current_quota: number | null;
}

export interface SavedPolicy {
  id: string;
  name: string;
  policy: QuotaPolicy;
  created_at: string;
  updated_at: string;
}

export interface PublishedPolicy {
  id: string;
  name: string;
  note: string | null;
  published_at: string;
  policy: QuotaPolicy;
}

export interface BaselineResult extends QuotaRunResponse {
  label: string | null;
  policy: QuotaPolicy;
}

export interface ModelState {
  open: boolean;
  dataAsAt: string | null;
  churchCount: number | null;
}

export interface AdminConfig {
  isOpen: boolean;
  opensAt: string | null;
  closesAt: string | null;
  baselineLabel: string | null;
  hasBaseline: boolean;
  updatedAt: string | null;
  dataAsAt: string | null;
  churchCount: number | null;
}

export interface AdminParticipant {
  id: string;
  email: string;
  display_name: string | null;
  church: string | null;
  organisation: string | null;
  created_at: string;
  last_seen_at: string | null;
  zoho_synced_at: string | null;
  policy_count: number;
  last_activity: string | null;
}

export interface ActivityRow {
  email?: string;
  action: string;
  detail: string | null;
  at: string;
}

export interface MeResponse {
  email: string;
  role: 'admin' | 'participant';
  displayName: string | null;
  church: string | null;
  organisation: string | null;
  needsIdentity: boolean;
}

// ── API ─────────────────────────────────────────────────────────────────────
export const api = {
  // public
  state: () => apiFetch<ModelState>('/api/state'),
  authRequestOtp: (email: string) =>
    apiFetch<{ ok: true }>('/api/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  authVerifyOtp: (email: string, code: string) =>
    apiFetch<{ ok: true; token: string; role: 'admin' | 'participant'; needsIdentity: boolean }>(
      '/api/auth/verify-otp',
      { method: 'POST', body: JSON.stringify({ email, code }) },
    ),

  // authenticated
  me: () => apiFetch<MeResponse>('/api/auth/me'),
  saveIdentity: (body: { displayName: string; church: string; organisation?: string }) =>
    apiFetch<{ ok: true }>('/api/auth/me', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => apiFetch<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  /** "Sign out all devices" — ends every session for the caller's email. */
  logoutAll: () => apiFetch<{ ok: true }>('/api/auth/logout-all', { method: 'POST' }),

  // model (gated by the kill switch server-side)
  churches: () => apiFetch<{ churches: ModelChurch[] }>('/api/model/churches'),
  categories: () => apiFetch<{ categories: ExclusionCategory[] }>('/api/model/categories'),
  whatif: (policy: QuotaPolicy, assessment_year: number) =>
    apiFetch<QuotaRunResponse>('/api/model/whatif', {
      method: 'POST',
      body: JSON.stringify({ policy, assessment_year }),
    }),
  baseline: () => apiFetch<{ baseline: BaselineResult | null }>('/api/model/baseline'),
  published: () => apiFetch<{ policies: PublishedPolicy[] }>('/api/model/published'),

  // admin (registrar only)
  adminConfig: () => apiFetch<AdminConfig>('/api/admin/config'),
  adminSetConfig: (body: {
    isOpen?: boolean;
    opensAt?: string | null;
    closesAt?: string | null;
    baseline?: { policy: QuotaPolicy; assessment_year: number; label?: string } | null;
  }) => apiFetch<{ ok: true }>('/api/admin/config', { method: 'POST', body: JSON.stringify(body) }),
  adminParticipants: () =>
    apiFetch<{ participants: AdminParticipant[] }>('/api/admin/participants'),
  adminParticipant: (id: string) =>
    apiFetch<{ participant: AdminParticipant; policies: SavedPolicy[]; activity: ActivityRow[] }>(
      `/api/admin/participant/${encodeURIComponent(id)}`,
    ),
  adminActivity: () => apiFetch<{ activity: ActivityRow[] }>('/api/admin/activity'),
  adminPublish: (name: string, note: string, policy: QuotaPolicy) =>
    apiFetch<{ ok: true; id: string }>('/api/admin/published', {
      method: 'POST',
      body: JSON.stringify({ name, note, policy }),
    }),
  adminUnpublish: (id: string) =>
    apiFetch<{ ok: true }>(`/api/admin/published/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminBroadcast: (subject: string, message: string) =>
    apiFetch<{ ok: true; recipients: number; sent: number }>('/api/admin/broadcast', {
      method: 'POST',
      body: JSON.stringify({ subject, message }),
    }),

  // participant-owned policies (server scopes to the caller)
  listMyPolicies: () => apiFetch<{ policies: SavedPolicy[] }>('/api/model/policies'),
  saveMyPolicy: (name: string, policy: QuotaPolicy) =>
    apiFetch<{ ok: true; id: string }>('/api/model/policies', {
      method: 'POST',
      body: JSON.stringify({ name, policy }),
    }),
  updateMyPolicy: (id: string, name: string, policy: QuotaPolicy) =>
    apiFetch<{ ok: true }>(`/api/model/policies/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ name, policy }),
    }),
  deleteMyPolicy: (id: string) =>
    apiFetch<{ ok: true }>(`/api/model/policies/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};
